import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { History, Loader2, Search, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { listWhatsappHistory } from "@/lib/api/whatsapp.functions";
import { brToday, brIsoDate } from "@/lib/tz";

export const Route = createFileRoute("/_authenticated/m/historico-envios")({
  component: Page,
});

type Row = {
  id: string;
  sent_at: string;
  recipient_name: string | null;
  recipient_phone: string | null;
  message: string | null;
  status: string | null;
  error: string | null;
};

function defaultFrom() {
  return brIsoDate(new Date(Date.now() - 7 * 86400000));
}
function defaultTo() {
  return brToday();
}

function Page() {
  const _list = useServerFn(listWhatsappHistory);
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState(defaultTo());
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState<Row | null>(null);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const res = (await _list({
        data: {
          page: p,
          pageSize,
          from: from ? new Date(from + "T00:00:00").toISOString() : undefined,
          to: to ? new Date(to + "T23:59:59").toISOString() : undefined,
          status,
          query,
        },
      })) as any;
      setRows(res.rows);
      setTotal(res.total);
      setPage(res.page);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1); /* eslint-disable-next-line */
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
        <History className="w-6 h-6 text-green-600" /> Histórico de Envios
      </h1>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
          <CardDescription>Mensagens são mantidas por 90 dias.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">De</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Até</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="sent">Enviado</SelectItem>
                  <SelectItem value="failed">Erro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 md:col-span-1">
              <Label className="text-xs">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  className="pl-7 h-9"
                  placeholder="Nome ou telefone"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load(1)}
                />
              </div>
            </div>
            <Button className="h-9" onClick={() => load(1)} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aplicar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="p-2">Data/Hora</th>
                  <th className="p-2">Destinatário</th>
                  <th className="p-2">Telefone</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Prévia</th>
                  <th className="p-2 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Nenhuma mensagem encontrada.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-muted/30">
                    <td className="p-2 whitespace-nowrap">{format(new Date(r.sent_at), "dd/MM/yyyy HH:mm")}</td>
                    <td className="p-2 font-medium">{r.recipient_name || "—"}</td>
                    <td className="p-2">{r.recipient_phone || "—"}</td>
                    <td className="p-2">
                      {r.status === "sent" ? (
                        <Badge className="bg-green-600">Enviado</Badge>
                      ) : (
                        <Badge variant="destructive">Erro</Badge>
                      )}
                    </td>
                    <td className="p-2 max-w-[320px] truncate text-muted-foreground">
                      {(r.message || "").slice(0, 100)}
                    </td>
                    <td className="p-2">
                      <Button size="sm" variant="ghost" onClick={() => setPreview(r)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between p-3 border-t text-sm">
            <span className="text-muted-foreground">
              {total === 0 ? "0" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)}`} de {total}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => load(page - 1)}>
                <ChevronLeft className="w-4 h-4" /> Anterior
              </Button>
              <span className="text-xs text-muted-foreground">Página {page} de {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => load(page + 1)}>
                Próxima <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Mensagem enviada</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Data</Label><div>{format(new Date(preview.sent_at), "dd/MM/yyyy HH:mm")}</div></div>
                <div><Label className="text-xs">Status</Label><div>{preview.status === "sent" ? "Enviado" : "Erro"}</div></div>
                <div><Label className="text-xs">Destinatário</Label><div>{preview.recipient_name || "—"}</div></div>
                <div><Label className="text-xs">Telefone</Label><div>{preview.recipient_phone || "—"}</div></div>
              </div>
              <div>
                <Label className="text-xs">Mensagem</Label>
                <div className="mt-1 p-3 bg-muted rounded text-xs whitespace-pre-wrap break-words max-h-[300px] overflow-auto">
                  {preview.message || "—"}
                </div>
              </div>
              {preview.error && (
                <div>
                  <Label className="text-xs text-destructive">Erro</Label>
                  <div className="mt-1 p-2 bg-destructive/10 text-destructive rounded text-xs">{preview.error}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
